import { useEffect, useState } from "react"
import { API_BASE_URL } from "../config"
import { useNavigate } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import { SiteHeader } from "@/components/site-header"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { logoutUser, getSession, getToken } from "@/lib/auth"
import { fetchUsers, createUser, deleteUser, fetchLogs, fetchDbStatus, triggerDbBackup } from "@/lib/sysadminApi"
import { UsersIcon, ActivityIcon, DatabaseIcon, PlusIcon, TrashIcon, RefreshCwIcon, DownloadIcon } from "lucide-react"

export function SystemAdminDashboard() {
  const navigate = useNavigate()
  const session = getSession()

  const [users, setUsers] = useState<any[]>([])
  const [logs, setLogs] = useState<any[]>([])
  const [dbStatus, setDbStatus] = useState<any>(null)
  
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [loadingDb, setLoadingDb] = useState(false)
  const [backingUp, setBackingUp] = useState(false)

  // New user form state
  const [newEmail, setNewEmail] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [newFullName, setNewFullName] = useState("")
  const [newRole, setNewRole] = useState("")
  
  const [logFilter, setLogFilter] = useState("all")

  useEffect(() => {
    loadUsers()
    loadLogs()
    loadDbStatus()
  }, [])

  async function loadUsers() {
    const token = getToken()
    if (!token) return
    try {
      setLoadingUsers(true)
      const data = await fetchUsers(token)
      setUsers(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingUsers(false)
    }
  }

  async function loadLogs() {
    const token = getToken()
    if (!token) return
    try {
      setLoadingLogs(true)
      const data = await fetchLogs(token)
      setLogs(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingLogs(false)
    }
  }

  async function loadDbStatus() {
    const token = getToken()
    if (!token) return
    try {
      setLoadingDb(true)
      const data = await fetchDbStatus(token)
      setDbStatus(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingDb(false)
    }
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault()
    const token = getToken()
    if (!token) return
    try {
      await createUser(token, {
        email: newEmail,
        password: newPassword,
        full_name: newFullName,
        role: newRole
      })
      alert("Usuario creado exitosamente")
      setNewEmail("")
      setNewPassword("")
      setNewFullName("")
      setNewRole("")
      loadUsers()
      loadLogs()
    } catch (err) {
      alert("Error al crear usuario")
    }
  }

  async function handleDeleteUser(id: number) {
    if (!confirm("¿Seguro que desea eliminar este usuario?")) return
    const token = getToken()
    if (!token) return
    try {
      await deleteUser(token, id)
      alert("Usuario eliminado")
      loadUsers()
      loadLogs()
    } catch (err) {
      alert("Error al eliminar usuario")
    }
  }

  async function handleBackup() {
    const token = getToken()
    if (!token) return
    try {
      setBackingUp(true)
      const data = await triggerDbBackup(token)
      alert(data.message)
      if (data.download_url) {
        window.open(`${API_BASE_URL}${data.download_url}`, "_blank")
      }
      loadLogs()
    } catch (err: any) {
      alert(err.message || "Error al respaldar base de datos")
    } finally {
      setBackingUp(false)
    }
  }

  function cerrarSesion() {
    logoutUser()
    navigate("/login", { replace: true })
  }

  const filteredLogs = logFilter === "all" ? logs : logs.filter(l => l.level === logFilter)

  return (
    <SidebarProvider>
      <AppSidebar
        user={{
          name: session?.fullName ?? "SysAdmin",
          email: session?.email ?? "sysadmin@inseso.gq",
        }}
        role={session?.role}
        onLogout={cerrarSesion}
      />
      <SidebarInset>
        <SiteHeader title="Panel de Administración del Sistema (SysAdmin)" />
        <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="mb-4 bg-muted/50 p-1">
              <TabsTrigger value="users" className="gap-2"><UsersIcon className="size-4" /> Gestión de Usuarios</TabsTrigger>
              <TabsTrigger value="logs" className="gap-2"><ActivityIcon className="size-4" /> Bitácora del Sistema</TabsTrigger>
              <TabsTrigger value="db" className="gap-2"><DatabaseIcon className="size-4" /> Base de Datos</TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-[1fr_300px]">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Usuarios Registrados</CardTitle>
                      <CardDescription>Lista de usuarios del sistema y sus roles.</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadUsers} disabled={loadingUsers}>
                      <RefreshCwIcon className={`size-4 mr-2 ${loadingUsers ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Rol</TableHead>
                          <TableHead>Registro</TableHead>
                          <TableHead className="text-right">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map(u => (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.email}</TableCell>
                            <TableCell>{u.full_name || "-"}</TableCell>
                            <TableCell>
                              {u.role ? (
                                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                  {u.role}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">Sin Rol</span>
                              )}
                            </TableCell>
                            <TableCell>{u.registration_date}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} title="Eliminar">
                                <TrashIcon className="size-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Nuevo Usuario</CardTitle>
                  </CardHeader>
                  <form onSubmit={handleCreateUser}>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Email</label>
                        <Input required type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Contraseña</label>
                        <Input required type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Nombre Completo</label>
                        <Input value={newFullName} onChange={e => setNewFullName(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Rol</label>
                        <Select value={newRole} onValueChange={setNewRole}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar rol" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="affiliate">Afiliado</SelectItem>
                            <SelectItem value="administrator">Administrador</SelectItem>
                            <SelectItem value="system_admin">SysAdmin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button type="submit" className="w-full">
                        <PlusIcon className="size-4 mr-2" />
                        Crear Usuario
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="logs">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Bitácora del Sistema (Logs Reales)</CardTitle>
                    <CardDescription>Monitoreo de actividad y errores del servidor.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={logFilter} onValueChange={setLogFilter}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Filtrar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="INFO">INFO</SelectItem>
                        <SelectItem value="WARNING">WARNING</SelectItem>
                        <SelectItem value="ERROR">ERROR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={loadLogs} disabled={loadingLogs}>
                      <RefreshCwIcon className={`size-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-48">Timestamp</TableHead>
                          <TableHead className="w-24">Nivel</TableHead>
                          <TableHead className="w-48">Acción/Módulo</TableHead>
                          <TableHead>Detalles</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLogs.map(l => (
                          <TableRow key={l.id}>
                            <TableCell className="text-xs text-muted-foreground">{l.timestamp}</TableCell>
                            <TableCell>
                              <span className={`text-xs font-semibold ${l.level === 'ERROR' ? 'text-destructive' : l.level === 'WARNING' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {l.level}
                              </span>
                            </TableCell>
                            <TableCell className="font-medium text-xs">{l.action}</TableCell>
                            <TableCell className="text-sm font-mono">{l.details}</TableCell>
                          </TableRow>
                        ))}
                        {filteredLogs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                              No hay registros.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="db">
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Estado de Base de Datos</CardTitle>
                    <CardDescription>Métricas y salud del motor de base de datos PostgreSQL</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loadingDb ? (
                      <p className="text-sm">Cargando métricas...</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/30 rounded-lg border">
                          <span className="block text-xs text-muted-foreground">Estado</span>
                          <span className="text-xl font-semibold text-green-600">{dbStatus?.status || "Saludable"}</span>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg border">
                          <span className="block text-xs text-muted-foreground">Último Respaldo</span>
                          <span className="text-xl font-semibold">{dbStatus?.last_backup || "N/A"}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Respaldo Manual (Backup)</CardTitle>
                    <CardDescription>Genera una copia de seguridad física de la base de datos.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Esta acción ejecuta <code>pg_dump</code> en el servidor para crear un volcado SQL completo de la base de datos actual y genera un enlace de descarga segura.
                    </p>
                    <Button onClick={handleBackup} disabled={backingUp} className="w-full">
                      <DownloadIcon className="size-4 mr-2" />
                      {backingUp ? "Ejecutando volcado..." : "Generar Respaldo Ahora"}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
